var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState } from 'react';
import { BooleanInput, EditView, SimpleForm, Toolbar, TextField, TextInput, EditButton, SaveButton, required, maxLength, email, useEditController, useTranslate, FormDataConsumer, useQuery, } from 'react-admin';
import { Link } from 'react-router-dom';
import { makeStyles, Collapse } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import createDecorator from 'final-form-calculate';
import get from 'lodash/get';
import { FormDialog, ViewTitle, PasswordInput } from '../ui';
import { usePasswordEncryption, getPwdTooltip } from '../authentication';
import { regex } from 'ra-core';
import { UserProfileTitle } from './UserProfileTitle';
import { UserProfileSubtitle } from './UserProfileSubtitle';
import { isPortalAdmin, useUserContext } from '../userContexts';
export const UserProfileEdit = props => {
    const _a = useEditController(Object.assign({ successMessage: 'resources.userProfiles.notifications.update_success' }, props)), { record, save } = _a, editControllerProps = __rest(_a, ["record", "save"]);
    const translate = useTranslate();
    const [publicKey, encrypt] = usePasswordEncryption();
    const [currentPassword, setCurrentPassword] = useState('');
    const [showFormDialog, setShowFormDialog] = useState(false);
    const [savedFormData, setSavedFormData] = useState({});
    const classes = useUserProfileEditFormStyles();
    const [currentPwdError, setCurrentPwdError] = useState(false);
    const handleSave = async (formData) => {
        const { newPassword } = formData, userProfile = __rest(formData, ["newPassword"]);
        // if currentPassword and newPassword are available, then user
        // is updating password
        // if only currentPassword is available, then user is updating email
        if (newPassword && currentPassword) {
            if (publicKey) {
                userProfile.newPassword = await encrypt(newPassword);
                userProfile.password = await encrypt(currentPassword);
                userProfile.publicKey = publicKey;
            }
            else {
                userProfile.newPassword = newPassword;
                userProfile.password = currentPassword;
            }
        }
        else if (currentPassword) {
            if (publicKey) {
                userProfile.password = await encrypt(currentPassword);
                userProfile.publicKey = publicKey;
            }
            else {
                userProfile.password = await encrypt(currentPassword);
            }
        }
        const result = await save(userProfile, 'show');
        return result;
    };
    const handleSubmit = async (formData) => {
        const { email, newPassword, updatePassword } = formData;
        if (get(record, 'email') !== email || (updatePassword && newPassword)) {
            setSavedFormData(formData);
            setShowFormDialog(true);
        }
        else {
            await handleSave(formData);
        }
    };
    const handleFormDialogCancel = () => {
        setShowFormDialog(false);
        setCurrentPwdError(false);
        setCurrentPassword('');
    };
    const handleFormDialogSubmit = async () => {
        if (currentPassword) {
            setShowFormDialog(false);
            await handleSave(savedFormData);
        }
        else {
            setCurrentPwdError(true);
        }
    };
    const handleFormDialogFormSubmit = async (event) => {
        event.preventDefault();
        if (currentPassword) {
            setShowFormDialog(false);
            await handleSave(savedFormData);
        }
        else {
            setCurrentPwdError(true);
        }
    };
    const handleCurrentPasswordChange = event => {
        setCurrentPwdError(false);
        setCurrentPassword(event.target.value);
    };
    // workaround to hide form toolbar in formdialog
    const EmptyDiv = () => React.createElement("div", null);
    // specified those props to avoid errors in console
    const ToolbarCustomEmptyDiv = (_a) => {
        var { handleSubmit, handleSubmitWithRedirect, onSave, invalid, pristine, saving, submitOnEnter } = _a, rest = __rest(_a, ["handleSubmit", "handleSubmitWithRedirect", "onSave", "invalid", "pristine", "saving", "submitOnEnter"]);
        return React.createElement(EmptyDiv, Object.assign({}, rest));
    };
    const modalFormValidate = ({ currentPassword }) => {
        const errors = {};
        if (!currentPassword) {
            errors.currentPassword =
                'resources.userProfiles.validation.error_password_empty';
        }
        return errors;
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(ViewTitle, null),
        React.createElement(EditView, Object.assign({ undoable: false }, editControllerProps, { title: React.createElement(UserProfileTitle, { actions: React.createElement(EditButton, { disabled: true }) }), record: record, save: handleSubmit }),
            React.createElement(UserProfileEditForm, null)),
        React.createElement(FormDialog, { isOpen: showFormDialog, submitText: translate('resources.userProfiles.actions.submit'), cancelText: translate('resources.userProfiles.actions.cancel'), onSubmit: handleFormDialogSubmit, onCancel: handleFormDialogCancel, title: translate('resources.userProfiles.passwordDialogTitle'), submitButtonDisabled: false, isDialogContentText: false },
            React.createElement(SimpleForm, { toolbar: React.createElement(ToolbarCustomEmptyDiv, null), validate: modalFormValidate, onSubmit: handleFormDialogFormSubmit },
                React.createElement(PasswordInput, { className: classes.field, id: 'currentPassword', name: 'currentPassword', value: currentPassword, fullWidth: true, helperText: "resources.userProfiles.validation.tooltip_current_password", label: "resources.userProfiles.passwordDialogTitle", error: currentPwdError, onChange: handleCurrentPasswordChange, variant: "filled", style: {
                        width: 448,
                    } })))));
};
const CancelButton = ({ record }) => {
    const translate = useTranslate();
    return (React.createElement(Button, { component: Link, to: record ? `/userProfiles/${record.id}/show` : '', variant: "outlined", color: "primary" }, translate('resources.userProfiles.actions.cancel')));
};
const useUserProfileEditToolbarStyles = makeStyles(theme => ({
    toolbar: {
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: 456,
    },
    saveButton: {
        marginLeft: theme.spacing(2),
    },
}), {
    name: 'Layer7UserContextEditToolbar',
});
const UserProfileEditToolbar = (_a) => {
    var { record } = _a, rest = __rest(_a, ["record"]);
    const classes = useUserProfileEditToolbarStyles();
    const [userContext] = useUserContext();
    const isIdpUser = get(userContext, 'userDetails.isIdpUser', false);
    return (React.createElement(Toolbar, Object.assign({ className: classes.toolbar, record: record }, rest),
        React.createElement(CancelButton, { disabled: isIdpUser }),
        React.createElement(SaveButton, { disabled: isIdpUser, className: classes.saveButton, redirect: "show" })));
};
const useUserProfileEditFormStyles = makeStyles(theme => ({
    root: {
        padding: `${theme.spacing(2)}px !important`,
        width: 480,
    },
    field: {
        display: 'block',
    },
}), {
    name: 'Layer7UserProfileEditForm',
});
const validateName = [required(), maxLength(60)];
const validateEmail = [required(), maxLength(256), email()];
const pwdResetDecorator = createDecorator({
    field: 'updatePassword',
    updates: {
        // ...clear new password and verified password if updatePassword is false
        newPassword: (updatePasswordValue, allValues) => updatePasswordValue ? allValues.newPassword : '',
        confirmNewPassword: (updatePasswordValue, allValues) => updatePasswordValue ? allValues.confirmNewPassword : '',
    },
});
export const UserProfileEditForm = (_a) => {
    var { record, basePath, save } = _a, rest = __rest(_a, ["record", "basePath", "save"]);
    const classes = useUserProfileEditFormStyles();
    const validate = ({ newPassword, confirmNewPassword, updatePassword }) => {
        const errors = {};
        if (updatePassword && newPassword) {
            if (newPassword !== confirmNewPassword) {
                errors.confirmNewPassword =
                    'resources.userProfiles.validation.error_password_match';
            }
        }
        if (updatePassword && !newPassword) {
            errors.newPassword =
                'resources.userProfiles.validation.error_password_empty';
        }
        return errors;
    };
    const [userContext] = useUserContext();
    const isAdmin = isPortalAdmin(userContext);
    const { data } = useQuery({
        type: 'getPasswordPolicy',
        resource: 'userProfiles',
    });
    const regexConfig = get(data, 'regexConfig', {});
    const regexStr = get(regexConfig, 'REGEX.value', '');
    const validatePassword = regexStr
        ? [
            regex(new RegExp(regexStr), 'resources.userProfiles.validation.error_password_not_matching_criteria'),
        ]
        : [];
    const translate = useTranslate();
    const passwordTooltip = getPwdTooltip(regexConfig, translate);
    const isIdpUser = get(userContext, 'userDetails.isIdpUser', false);
    return (React.createElement(SimpleForm, Object.assign({ className: classes.root, decorators: [pwdResetDecorator], record: record, save: save, toolbar: React.createElement(UserProfileEditToolbar, null), validate: validate }, rest),
        React.createElement(TextField, { className: classes.field, fullWidth: true, source: "userName" }),
        React.createElement(TextInput, { className: classes.field, fullWidth: true, source: "lastName", validate: validateName, disabled: isIdpUser }),
        React.createElement(TextInput, { className: classes.field, fullWidth: true, source: "firstName", validate: validateName, disabled: isIdpUser }),
        React.createElement(TextInput, { className: classes.field, fullWidth: true, source: "email", validate: validateEmail, disabled: !isAdmin || isIdpUser }),
        !isIdpUser && React.createElement(UserProfileSubtitle, null),
        !isIdpUser && (React.createElement(BooleanInput, { className: classes.field, fullWidth: true, source: "updatePassword" })),
        !isIdpUser && (React.createElement(FormDataConsumer, null, (_a) => {
            var { formData, className } = _a, rest = __rest(_a, ["formData", "className"]);
            return (React.createElement(Collapse, { in: formData.updatePassword },
                React.createElement(PasswordInput, Object.assign({ fullWidth: true, source: "newPassword", style: {
                        width: 448,
                    }, title: passwordTooltip, "data-testid": 'new-password', className: className, validate: validatePassword }, rest)),
                React.createElement("br", null),
                React.createElement(PasswordInput, Object.assign({ fullWidth: true, source: "confirmNewPassword", style: {
                        width: 448,
                    }, title: "resources.userProfiles.validation.tooltip_password_confirm", "data-testid": 'confirm-new-password', className: className }, rest))));
        }))));
};
//# sourceMappingURL=UserProfileEdit.js.map